import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ItemKind } from "@csf/shared";
import { useItems, useTagsWithCounts } from "../api/items.js";
import { ItemCard } from "../components/ItemCard.js";
import { KindTabs } from "../components/KindTabs.js";
import { ErrorState, Loading } from "../components/Loading.js";
import { SystemStatusCard } from "../components/SystemStatusCard.js";

export function ListPage() {
  const [kind, setKind] = useState<ItemKind | "all">("all");
  const [tag, setTag] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const itemsQuery = useItems({
    ...(kind === "all" ? {} : { kind }),
    ...(tag ? { tag } : {}),
  });
  const tagsQuery = useTagsWithCounts();

  const filtered = useMemo(() => {
    const items = itemsQuery.data ?? [];
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((it) => it.displayName.toLowerCase().includes(q));
  }, [itemsQuery.data, search]);

  return (
    <div className="space-y-3 p-3">
      <SystemStatusCard />
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Предметы</h1>
        <Link
          to="/add"
          className="rounded-lg bg-button px-3 py-1.5 text-sm font-medium text-button-text"
        >
          + Добавить
        </Link>
      </div>

      <KindTabs value={kind} onChange={setKind} />

      {tagsQuery.data && tagsQuery.data.length > 0 && (
        <div className="-mx-1 flex flex-wrap gap-1.5 overflow-x-auto px-1">
          <TagChip
            label="Все теги"
            active={tag === null}
            onClick={() => setTag(null)}
          />
          {tagsQuery.data.map((t) => (
            <TagChip
              key={t.tag}
              label={`${t.tag} (${t.count})`}
              active={tag === t.tag}
              onClick={() => setTag(tag === t.tag ? null : t.tag)}
            />
          ))}
        </div>
      )}

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Поиск по имени..."
        className="w-full rounded-xl bg-bg-secondary px-4 py-2 text-sm outline-none placeholder:text-hint"
      />

      {itemsQuery.isLoading && <Loading />}
      {itemsQuery.isError && <ErrorState error={itemsQuery.error} />}
      {itemsQuery.isSuccess && filtered.length === 0 && (
        <div className="rounded-lg bg-bg-secondary p-6 text-center text-hint">
          {search ? "Ничего не нашлось" : "Пока нет отслеживаемых предметов"}
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((it) => (
          <ItemCard key={it.id} item={it} />
        ))}
      </div>
    </div>
  );
}

function TagChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition ${
        active
          ? "bg-button text-button-text font-medium"
          : "bg-bg-secondary text-text"
      }`}
    >
      {label}
    </button>
  );
}
